import { useCallback, useState } from 'react';
import { debounce } from 'lodash';
import {
  CalculateFinancialSummaryRequest,
  CouponLineItem,
  CustomProductFeeRequest,
  DamageWaiverLineItem,
  DeliveryLineItem,
  DiscountLineItem,
  FeeLineItem,
  FinancialSummary,
  PickupLineItem,
  ProductFeeLineItem,
  ProductLineItem,
  SecurityDepositLineItem,
  TaxLineItem,
} from '../../../../clients/quipli-service/financials/financials';
import { Location } from '../../../../clients/quipli-service/location/location';
import { Currency } from 'dinero.js';
import { convertToMoney, safeRound } from '../../../../util/util';
import {
  Order,
  OrderAdditionalFee,
  OrderProductAdditionalFee,
  OrderType,
} from '../../../../clients/quipli-service/order/order';
import { TaxCode } from '../../../../clients/quipli-service/tax/tax';
import { Configuration } from '../../../../clients/quipli-service/configuration/configuration';
import { useTaxApiV1 } from '../../../../clients/quipli-service/tax/tax-api-v1';
import { ShippingType } from '../../../../clients/quipli-service/delivery/delivery';
import { UserAddress } from '../../../../clients/quipli-service/user/user';
import { OrderInputItem } from '../../../../clients/quipli-service/order/order-calculation';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';

interface UseFinancialSummaryProps {
  order: Order;
  location: Location;
  configurations: Configuration[];
  showTaxInclusive: boolean;
}

export const useFinancialSummary = ({
  order,
  location,
  configurations,
  showTaxInclusive,
}: UseFinancialSummaryProps) => {
  const { getTaxCode } = useTaxApiV1();
  const { calculateFinancialSummary } = useAPI();
  const [customDelivery, setCustomDelivery] = useState<Dinero.Dinero | undefined>(undefined);
  const [customPickup, setCustomPickup] = useState<Dinero.Dinero | undefined>(undefined);
  const [customDamageWaiver, setCustomDamageWaiver] = useState<Dinero.Dinero | undefined>(undefined);
  const [customFees, setCustomFees] = useState<{ feeId: number; amount: number }[]>([]);
  const [customAdditionalDiscount, setCustomAdditionalDiscount] = useState<Dinero.Dinero | undefined>(undefined);
  const [customTax, setCustomTax] = useState<Dinero.Dinero | undefined>(undefined);
  const [customTaxPercentage, setCustomTaxPercentage] = useState<number | undefined>(undefined);
  const [coupon, setCoupon] = useState<string>('');
  const [selectedDelivery, setSelectedDelivery] = useState<ShippingType | null>(null);
  const [customProductFees, setCustomProductFees] = useState<CustomProductFeeRequest[]>([]);
  const [summary, setSummary] = useState<FinancialSummary | undefined>(undefined);
  const [taxCode, setTaxCode] = useState<{ id?: string; name?: string; taxCodeSource?: string } | undefined>({
    id: order.taxCode,
  });
  const [existingSecurityDeposit, setExistingSecurityDeposit] = useState<SecurityDepositLineItem | undefined>(
    undefined,
  );
  const [prevSummary, setPrevSummary] = useState<FinancialSummary | undefined>(undefined);

  const setInitStatesValues = () => {
    const currency = location.currency as Currency;

    // Delivery
    setCustomDelivery(
      order.shippingUpdatedAt && order.shippingUpdatedAt.toISO() !== null
        ? convertToMoney(order.shippingCharged || 0, currency)
        : undefined,
    );

    setCustomPickup(
      order.pickupUpdatedAt && order.pickupUpdatedAt.toISO() !== null
        ? convertToMoney(order.pickupCharged || 0, currency)
        : undefined,
    );

    // Tax
    setCustomTaxPercentage(
      !!order.taxPercentageUpdatedAt && !!order.taxPercentage ? safeRound(order.taxPercentage * 100, 2) : undefined,
    );
    setCustomTax(
      order.taxUpdatedAt && order.taxUpdatedAt.toISO() !== null
        ? convertToMoney(order.taxCharged, location.currency as Currency)
        : undefined,
    );

    // Coupon
    setCoupon(order.discountCoupon || '-NA-');

    // Damage Waiver
    setCustomDamageWaiver(
      order.damageWaiverUpdatedAt && order.damageWaiverUpdatedAt.toISO() !== null
        ? convertToMoney(order.damageWaiver || 0, currency)
        : undefined,
    );

    // Additional Fees
    setCustomFees(
      order.additionalFees
        .filter((fee) => fee.updatedAt && fee.updatedAt.toISO() !== null)
        .map((fee) => ({
          feeId: fee.feeId,
          amount: convertToMoney(fee.feeAppliedValue || 0, currency).getAmount(),
        })),
    );

    // Product Fees
    setCustomProductFees(
      order.products.flatMap((product) =>
        product.additionalFees
          .filter((fee) => fee.updatedAt && fee.updatedAt.toISO() !== null)
          .map((fee) => ({
            feeId: fee.attributeId,
            productId: product.product.id,
            productLineItemId: `${product.id}`,
            amount: convertToMoney(fee.feeAppliedValue || 0, currency).getAmount(),
          })),
      ),
    );

    // Additional Discount
    setCustomAdditionalDiscount(
      order.additionalDiscount && order.additionalDiscount > 0
        ? convertToMoney(order.additionalDiscount, currency)
        : undefined,
    );

    // Delivery Type
    setSelectedDelivery(order.products[0].shippingType);
  };

  const calculateOnLoad = useCallback(
    debounce(
      async (
        initProductPrices: () => Promise<void>,
        billingAddress: UserAddress | undefined,
        deliveryAddress: UserAddress | undefined,
        orderInputs: OrderInputItem[],
      ) => {
        setInitStatesValues();

        const currency = location.currency as Currency;
        // Get available delivery options for each product and product prices

        await initProductPrices();

        // Set initial summary
        const productFeeLineItems: ProductFeeLineItem[] = [];

        const productLineItems: ProductLineItem[] = order.products.map((product) => {
          const additionalFees = product.additionalFees.map(
            (fee: OrderProductAdditionalFee) =>
              ({
                productLineItemId: `${product.id}`,
                productFeeId: +fee.id,
                productId: +fee.productId,
                description: fee.feeTitle,
                type: 'ProductFee',
                percentage: fee.feeType === 'P' ? fee.feeValue || 0 : 0,
                taxable: fee.taxApplied,
                unitPrice: convertToMoney(+fee.feeAppliedValue, currency),
                amount: convertToMoney(+fee.feeAppliedValue, currency),
                recurring: fee.recurring,
                hasCustomAmount: !!fee.updatedAt,
              } as ProductFeeLineItem),
          );

          productFeeLineItems.push(...additionalFees);

          return {
            id: `${product.id}`,
            productId: +product.product.id,
            type: 'Product',
            description: product.name,
            transactionType: product.type,
            unitPrice: convertToMoney(+product.customerBuyingPrice || 0, currency),
            quantity: +product.quantity,
            taxable: !product.isTaxFree,
            tax: convertToMoney(+product.tax, currency),
            amount: convertToMoney(
              +product.customerBuyingPrice *
                +product.quantity *
                (product.discountPercentage ? +(1 - product.discountPercentage / 100) : 1),
              currency,
            ),
            hasCustomAmount: false,
            taxCategoryId: product.taxCategoryId,
          } as ProductLineItem;
        });

        const feeLineItems: FeeLineItem[] = order.additionalFees.map(
          (fee: OrderAdditionalFee) =>
            ({
              id: fee.id,
              feeId: +fee.feeId,
              description: fee.feeTitle,
              type: 'Fee',
              percentage: fee.feeType === 'P' ? +fee.feeValue || 0 : 0,
              taxable: fee.taxApplied,
              optional: fee.optional,
              isSelected: true,
              unitPrice: convertToMoney(+fee.feeAppliedValue, currency),
              amount: convertToMoney(+fee.feeAppliedValue, currency),
              hasCustomAmount: !!fee.updatedAt,
              quantity: 1,
              recurring: fee.recurring,
            } as FeeLineItem),
        );

        const orderDamageWaiver: DamageWaiverLineItem = {
          type: 'DamageWaiver',
          hasCustomAmount: !!order.damageWaiverUpdatedAt,
          description: 'Damage Waiver',
          quantity: 1,
          taxable: order.applyDamageWaiverTax,
          unitPrice: convertToMoney(order.damageWaiver || 0, currency),
          amount: convertToMoney(order.damageWaiver || 0, currency),
          recurring: order.damageWaiverRecurring,
          optional: false,
          isSelected: true,
        };

        const orderAdditionalDiscount: DiscountLineItem = {
          type: 'AdditionalDiscount',
          description: 'Additional Discount',
          quantity: 1,
          taxable: false,
          unitPrice: convertToMoney(-(order.additionalDiscount || 0), currency),
          amount: convertToMoney(-(order.additionalDiscount || 0), currency),
          recurring: false,
        };

        const deliveryFee: DeliveryLineItem = {
          type: 'Delivery',
          hasCustomAmount: !!order.shippingUpdatedAt,
          description: 'Delivery',
          quantity: 1,
          taxable: false,
          unitPrice: convertToMoney(order.shippingCharged || 0, currency),
          amount: convertToMoney(order.shippingCharged || 0, currency),
          recurring: false,
        };

        const pickupFee: PickupLineItem = {
          type: 'Pickup',
          hasCustomAmount: !!order.pickupUpdatedAt,
          description: 'Pickup',
          quantity: 1,
          taxable: false,
          unitPrice: convertToMoney(order.pickupCharged, currency),
          amount: convertToMoney(order.pickupCharged, currency),
          recurring: false,
        };

        let taxRate: TaxCode | undefined;
        if (order.taxCode) {
          taxRate = await getTaxCode(order.taxCode);
          setTaxCode({ id: order.taxCode, name: taxRate.name, taxCodeSource: taxRate.source });
        }

        const taxFee: TaxLineItem = {
          type: 'Tax',
          hasCustomAmount: !!order.taxUpdatedAt,
          hasCustomPercentage: !!order.taxPercentageUpdatedAt,
          taxBreakdown: order.taxBreakdown,
          taxCodeId: order.taxCode,
          description: 'Tax',
          quantity: 1,
          taxable: false,
          userIsExempted: !order.user.chargeTax,
          percentage: safeRound((order.taxPercentage || 0) * 100, 2),
          unitPrice: convertToMoney(+order.taxCharged, currency),
          amount: convertToMoney(+order.taxCharged, currency),
          recurring: false,
        };

        const couponDiscount: CouponLineItem = {
          type: 'Coupon',
          description: 'Coupon',
          quantity: 1,
          taxable: true,
          unitPrice: convertToMoney(-(order.discountTotal || 0), currency),
          amount: convertToMoney(-(order.discountTotal || 0), currency),
          code: order.discountCoupon || '-NA-',
          productIds: [],
          recurring: false,
          isVip: false,
        };

        const securityDeposit: SecurityDepositLineItem = {
          type: 'SecurityDeposit',
          description: '',
          quantity: 1,
          taxable: false,
          unitPrice: convertToMoney(order.rentalSecurity || 0, currency),
          amount: convertToMoney(order.rentalSecurity || 0, currency),
          percentage: order.rentalSecurityPercentage,
          recurring: false,
        };

        setExistingSecurityDeposit(securityDeposit);

        let newSummary: FinancialSummary = {
          total: convertToMoney(order.netCharged, currency),
          lineItems: [
            ...productLineItems,
            ...productFeeLineItems,
            ...feeLineItems,
            orderDamageWaiver,
            orderAdditionalDiscount,
            pickupFee,
            deliveryFee,
            taxFee,
            couponDiscount,
            securityDeposit,
          ],
        };

        if (showTaxInclusive) {
          newSummary = await calculateFinancialSummary(
            getFinancialRequest(billingAddress, deliveryAddress, orderInputs),
          );
        }

        setSummary(newSummary);
        setPrevSummary(newSummary);
      },
      200,
    ),
    [
      order,
      location,
      configurations,
      coupon,
      customDelivery,
      customPickup,
      customDamageWaiver,
      customFees,
      customAdditionalDiscount,
      customTax,
      showTaxInclusive,
    ],
  );

  const getFinancialRequest = (
    billingAddress: UserAddress | undefined,
    deliveryAddress: UserAddress | undefined,
    orderInputs: OrderInputItem[],
  ): CalculateFinancialSummaryRequest => {
    return {
      customerId: order.user.id,
      locationId: location.id,
      customDelivery: customDelivery?.getAmount(),
      customPickup: customPickup?.getAmount(),
      customTax: customTax?.getAmount(),
      customTaxPercentage: customTaxPercentage,
      customDamageWaiver: customDamageWaiver?.getAmount(),
      customFees: customFees,
      customProductFees: customProductFees,
      couponCode: coupon,
      additionalDiscount: customAdditionalDiscount?.getAmount(),
      billingAddressId: billingAddress?.id,
      deliveryAddressId: deliveryAddress?.id,
      deliveryType: selectedDelivery || undefined,
      paymentMethod: order.paymentMethodType,
      isCycleBilled: order.isCycleBilled,
      cycleLength: order.cycleBillingTerms?.cycleLength,
      items: order.isCycleBilled
        ? orderInputs.map((oi) => {
            if (oi.type === OrderType.Rental) {
              return {
                ...oi,
                end: oi.start.plus(order.cycleBillingTerms!.cycleLength),
              };
            } else {
              return oi;
            }
          })
        : orderInputs,
      taxRateId: taxCode?.id,
      isTaxJar: taxCode?.taxCodeSource?.toLowerCase() === 'taxjar',
      securityDeposit: { amount: order.rentalSecurity, percentage: order.rentalSecurityPercentage },
      documentType: order.documentType,
      taxInclusive: showTaxInclusive,
    };
  };

  return {
    summary,
    setSummary,
    prevSummary,
    setPrevSummary,
    customDelivery,
    setCustomDelivery,
    customPickup,
    setCustomPickup,
    customDamageWaiver,
    setCustomDamageWaiver,
    customFees,
    setCustomFees,
    customAdditionalDiscount,
    setCustomAdditionalDiscount,
    customTax,
    setCustomTax,
    customTaxPercentage,
    setCustomTaxPercentage,
    coupon,
    setCoupon,
    selectedDelivery,
    setSelectedDelivery,
    customProductFees,
    setCustomProductFees,
    taxCode,
    setTaxCode,
    existingSecurityDeposit,
    setExistingSecurityDeposit,
    calculateOnLoad,
    setInitStatesValues,
    getFinancialRequest,
  };
};
